import { TextAlignment } from 'data/TextAlignment';
import { TextFrame } from 'data/TextFrame';
import {
  CompositionEffect,
  DIRECTION_HORIZONTAL,
  DIRECTION_VERTICAL,
  DURATION_LONG,
  DURATION_SUPER_LONG,
  DURATION_VERY_LONG,
  DURATION_VERY_VERY_LONG,
  EASING_BEZIER_IN_CUBIC,
  EASING_BEZIER_OUT_CUBIC,
  NO_DURATION
} from './CompositionEffect';
import { PropertyType } from '@beatflyer/shaders-lib';
import { splitTextIntoWords } from 'utils/PixiUtils';

const curveIn = EASING_BEZIER_OUT_CUBIC;
const curveOut = EASING_BEZIER_IN_CUBIC;

class Composition extends CompositionEffect {
  static id = 'composition-text';
  static label = 'Text';

  isText = true;

  composedFrame: TextFrame | null = null;

  view: PIXI.Container | null = null;
  initialYs: number[] = [];
  initialXs: number[] = [];

  destroyView() {
    if (this.composedFrame === null) {
      return;
    }
    if (this.view) {
      this.view.destroy(true);
      this.view = null;
    }
    this.composedFrame.sprite.visible = true;
    this.composedFrame = null;
  }

  buildView(frame: TextFrame) {
    if (this.view) {
      this.view.destroy(true);
    }
    this.view = new PIXI.Container();

    if (frame.text.trim().length > 0) {
      const { text, textStyle, boundingBox } = frame;
      splitTextIntoWords(this.view, text, textStyle, boundingBox);
      const verticalAlign = (textStyle as any).verticalAlign as TextAlignment;
      if (
        textStyle.align === TextAlignment.Center &&
        verticalAlign === TextAlignment.Middle
      ) {
        this.view!.rotation = frame.rotation / 57.2958;
      }
      this.initialYs = [];
      this.initialXs = [];
      this.view.children.forEach((child, index) => {
        this.initialYs[index] = child.y;
        this.initialXs[index] = child.x;
      });
    }
    //
    this.layer.sprite.addChild(this.view);
    this.composedFrame = frame;
  }

  protected compose(position: number) {
    const currentFrame = this.layer.currentFrame;
    if (!(currentFrame instanceof TextFrame)) {
      this.destroyView();
      return;
    }
    if (currentFrame !== this.composedFrame) {
      this.buildView(currentFrame);
    }
    if (this.view) {
      const overlap = this.getProperty('overlap') as number;
      const move = this.getProperty('move') as boolean;
      const distance = move ? currentFrame.textStyle.lineHeight : 0;
      const direction = this.getProperty('direction') as string;
      const composeWord = (
        wordView: PIXI.Text,
        index: number,
        phase: number
      ) => {
        const p = curveIn(phase);
        wordView.alpha = p;
        if (direction === DIRECTION_VERTICAL) {
          wordView.y = this.initialYs[index] + (1 - p) * distance;
        } else {
          wordView.x = this.initialXs[index] + (1 - p) * distance;
        }
      };
      const wordsCount = this.view.children.length;
      const totalPhases = 1 + (wordsCount - 1) * (1 - overlap);
      const phaseDuration = 1 / totalPhases;
      for (var i = 0; i < wordsCount; i++) {
        const start = Math.max(0, (1 - overlap) * phaseDuration * i);
        const wordView = this.view.children[i] as PIXI.Text;
        let phase = 0;
        if (position > start) {
          if (position > start + phaseDuration) {
            phase = 1;
          } else {
            phase = (position - start) / phaseDuration;
          }
        }
        composeWord(wordView, i, phase);
      }
      currentFrame.sprite.visible = false;
    }
  }
  protected decompose(position: number) {
    const currentFrame = this.layer.currentFrame;
    if (!(currentFrame instanceof TextFrame)) {
      this.destroyView();
      return;
    }
    if (currentFrame !== this.composedFrame) {
      this.buildView(currentFrame);
    }
    if (this.view) {
      const overlapIn = this.getProperty('overlap') as number;
      const sameOut = this.getProperty('sameOut') as boolean;
      const overlap = sameOut
        ? overlapIn
        : (this.getProperty('overlapOut') as number);
      const moveIn = this.getProperty('move') as boolean;
      const move = sameOut ? moveIn : (this.getProperty('moveOut') as boolean);
      const distance = move ? currentFrame.textStyle.lineHeight : 0;
      const direction = this.getProperty('direction') as string;
      const decomposeWord = (
        wordView: PIXI.Text,
        index: number,
        phase: number
      ) => {
        const p = curveOut(phase);
        wordView.alpha = 1 - p;
        if (direction === DIRECTION_VERTICAL) {
          wordView.y = this.initialYs[index] - p * distance;
        } else {
          wordView.x = this.initialXs[index] - p * distance;
        }
      };
      const wordsCount = this.view.children.length;
      const totalPhases = 1 + (wordsCount - 1) * (1 - overlap);
      const phaseDuration = 1 / totalPhases;
      for (var i = 0; i < wordsCount; i++) {
        const start = Math.max(0, (1 - overlap) * phaseDuration * i);
        const wordView = this.view.children[i] as PIXI.Text;
        let phase = 0;
        if (position > start) {
          if (position > start + phaseDuration) {
            phase = 1;
          } else {
            phase = (position - start) / phaseDuration;
          }
        }
        decomposeWord(wordView, i, phase);
      }
      currentFrame.sprite.visible = false;
    }
  }

  getInitialDuration(): string {
    return DURATION_VERY_LONG;
  }

  getDurationOptions(): string[] {
    return [
      NO_DURATION,
      DURATION_LONG,
      DURATION_VERY_LONG,
      DURATION_VERY_VERY_LONG,
      DURATION_SUPER_LONG
    ];
  }

  getPropertiesDescriptors() {
    const { inDuration, sameOut, outDuration } =
      super.getPropertiesDescriptors();
    return {
      inDuration,
      overlap: {
        label: 'Overlap In',
        type: PropertyType.NUMBER,
        initialValue: 0.8
      },
      move: {
        label: 'Move In',
        type: PropertyType.BOOLEAN,
        initialValue: true
      },
      sameOut,
      outDuration,
      overlapOut: {
        label: 'Overlap Out',
        type: PropertyType.NUMBER,
        initialValue: 0.8,
        shouldBeVisible: () => {
          return this.getProperty('sameOut') === false;
        }
      },
      moveOut: {
        label: 'Move Out',
        type: PropertyType.BOOLEAN,
        initialValue: true,
        shouldBeVisible: () => {
          return this.getProperty('sameOut') === false;
        }
      },
      direction: {
        type: PropertyType.SELECT,
        options: [DIRECTION_VERTICAL, DIRECTION_HORIZONTAL],
        initialValue: DIRECTION_VERTICAL,
        shouldBeVisible: () => {
          const move = this.getProperty('move');
          return move === true;
        }
      }
    };
  }

  reset() {
    super.reset();
    this.destroyView();
  }
}

(window as any).BeatFlyer.compositions[Composition.id] = Composition;



// WEBPACK FOOTER //
// ./src/compose/ComposeText.ts